import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// 1. Ant Design
import { Button, Card, DatePicker, Select, Skeleton, Space, Statistic, Table, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
// 2. External libraries
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
// 5. Types, utils, styles
import { REPORT_GROUP_BY_LABELS } from "@/types/company/report.common";
import styles from "@/styles/Company/Report.module.less";
const { Text } = Typography;
const moneyFormatter = new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatMoney = (value) => (value === null ? "—" : `${moneyFormatter.format(value)} ₽`);
const formatKwh = (value) => (value === null ? "—" : moneyFormatter.format(value));
const formatDateTime = (value) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "—");
const formatVehicle = (vehicle) => {
    if (!vehicle)
        return "—";
    const model = [vehicle.brand, vehicle.model].filter(Boolean).join(" ");
    if (!model)
        return vehicle.stateNumber ?? "—";
    return vehicle.stateNumber ? `${model}, ${vehicle.stateNumber}` : model;
};
const groupByOptions = Object.entries(REPORT_GROUP_BY_LABELS).map(([value, label]) => ({ value, label }));
const breakdownColumns = [
    {
        title: "Строка",
        dataIndex: "label",
        key: "label",
    },
    {
        title: "Заказов",
        dataIndex: "orders",
        key: "orders",
    },
    {
        title: "кВт⋅ч",
        key: "kwh",
        render: (_, row) => formatKwh(row.kwh),
    },
    {
        title: "Сумма ₽",
        key: "amount",
        render: (_, row) => formatMoney(row.amount),
    },
];
const registryColumns = [
    {
        title: "Завершён",
        key: "chargedAt",
        render: (_, order) => formatDateTime(order.chargedAt),
    },
    {
        title: "Номер",
        dataIndex: "orderNumber",
        key: "orderNumber",
    },
    {
        title: "Сотрудник",
        key: "employeeLabel",
        render: (_, order) => order.employeeLabel ?? order.clientName ?? "—",
    },
    {
        title: "Адрес",
        dataIndex: "address",
        key: "address",
        render: (value) => value ?? "—",
    },
    {
        title: "Автомобиль",
        key: "vehicle",
        render: (_, order) => formatVehicle(order.vehicle),
    },
    {
        title: "кВт⋅ч",
        key: "actualTotalKwh",
        render: (_, order) => formatKwh(order.actualTotalKwh),
    },
    {
        title: "Цена",
        key: "pricePerKwhSnapshot",
        render: (_, order) => formatMoney(order.pricePerKwhSnapshot),
    },
    {
        title: "Сбор",
        key: "serviceFeeSnapshot",
        render: (_, order) => formatMoney(order.serviceFeeSnapshot),
    },
    {
        title: "Сумма",
        key: "actualCost",
        render: (_, order) => formatMoney(order.actualCost),
    },
    {
        title: "Техник",
        key: "technicianName",
        render: (_, order) => order.technicianName ?? "—",
    },
    {
        title: "Счёт",
        key: "invoiceNumber",
        render: (_, order) => order.invoiceNumber ?? "—",
    },
];
/**
 * Панель «Отчёт по заказам» (P-8, UC1124/UC1135): фильтры периода/разреза, итоги, разбивка
 * и реестр заказов за период, выгрузка в Excel. Не зависит от `getRootStore` — переиспользуется
 * и на вкладке персонала (`ReportTab`), и в корп-кабинете (`CorpReportPage`).
 */
const ReportPanel = observer(({ store, companyName }) => {
    const { params, report, orders, isLoading, isExporting } = store;
    const totals = report?.totals ?? { orders: 0, kwh: 0, amount: 0 };
    const breakdownRows = report?.rows ?? [];
    return (_jsxs(Card, { title: "\u041E\u0442\u0447\u0451\u0442 \u043F\u043E \u0437\u0430\u043A\u0430\u0437\u0430\u043C", className: styles.section, children: [_jsxs(Space, { className: styles.filters, wrap: true, children: [_jsxs(Space, { direction: "vertical", size: 4, children: [_jsx(Text, { type: "secondary", children: "\u041F\u0435\u0440\u0438\u043E\u0434 \u0441" }), _jsx(DatePicker, { "data-testid": "corp-report-from", value: dayjs(params.dateFrom), format: "DD.MM.YYYY", allowClear: false, onChange: (value) => {
                                    if (value)
                                        store.setParams({ dateFrom: value.format("YYYY-MM-DD") });
                                } })] }), _jsxs(Space, { direction: "vertical", size: 4, children: [_jsx(Text, { type: "secondary", children: "\u043F\u043E" }), _jsx(DatePicker, { "data-testid": "corp-report-to", value: dayjs(params.dateTo), format: "DD.MM.YYYY", allowClear: false, onChange: (value) => {
                                    if (value)
                                        store.setParams({ dateTo: value.format("YYYY-MM-DD") });
                                } })] }), _jsxs(Space, { direction: "vertical", size: 4, children: [_jsx(Text, { type: "secondary", children: "\u0420\u0430\u0437\u0440\u0435\u0437" }), _jsx(Select, { "data-testid": "corp-report-group-by", value: params.groupBy, options: groupByOptions, className: styles.groupBy, onChange: (value) => store.setParams({ groupBy: value }) })] }), _jsx(Button, { "data-testid": "corp-report-export", icon: _jsx(DownloadOutlined, {}), loading: isExporting, onClick: () => void store.exportExcel(companyName), children: "\u0412\u044B\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0432 Excel" })] }), isLoading ? (_jsx(Skeleton, { active: true })) : (_jsxs(_Fragment, { children: [_jsxs(Space, { "data-testid": "corp-report-totals", size: "large", className: styles.totals, children: [_jsx(Statistic, { title: "\u0417\u0430\u043A\u0430\u0437\u043E\u0432", value: totals.orders }), _jsx(Statistic, { title: "\u043A\u0412\u0442\u22C5\u0447", value: formatKwh(totals.kwh) }), _jsx(Statistic, { title: "\u0421\u0443\u043C\u043C\u0430 \u20BD", value: formatMoney(totals.amount) })] }), _jsx("div", { "data-testid": "corp-report-table", className: styles.section, children: _jsx(Table, { rowKey: "key", columns: breakdownColumns, dataSource: breakdownRows, pagination: false, locale: { emptyText: "Заказов за период нет" }, size: "small" }) }), _jsx(Card, { title: "\u0420\u0435\u0435\u0441\u0442\u0440 \u0437\u0430\u043A\u0430\u0437\u043E\u0432 \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434", "data-testid": "corp-report-registry", children: _jsx(Table, { rowKey: "orderId", columns: registryColumns, dataSource: orders, pagination: { pageSize: 20 }, locale: { emptyText: "Заказов за период нет" }, size: "small", scroll: { x: true } }) })] }))] }));
});
ReportPanel.displayName = "ReportPanel";
export default ReportPanel;
